import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, filter, map, switchMap, tap } from 'rxjs';
import { resetAppState } from '../../../../store/helper-fns/reset-app-state';
import { AppState } from '../../../../store/state/app.state';
import { VersionService } from '../../../crash-report/services/api/version/version.service';
import { SnackbarService } from '../../../shared/modules/snackbar/services/snackbar/snackbar.service';
import { ConfigService } from '../../../shared/services/config/config.service';
import { LocalStorageService } from '../../../shared/services/local-storage/local-storage.service';
import { ServerErrorService } from '../../../shared/services/server-error/server-error.service';
import { SessionStorageService } from '../../../shared/services/session-storage/session-storage.service';
import { OssOptional } from '../../../shared/utils/oss-optional';
import { ApiCurrentUser } from '../../data-model/models/api-current-user.model';
import { ClientCurrentUser } from '../../data-model/models/client-current-user.model';
import { AuthService } from '../../services/api/auth/auth.service';
import { AuthActions } from '../actions';

@Injectable()
export class AuthEffects {
  $signIn = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.signIn),
      switchMap(action =>
        this.authService.signIn(action.username, action.password).pipe(
          map(currentUser =>
            AuthActions.signedIn({
              currentUser: this.mapCurrentUser(currentUser),
              apiToken: currentUser['oss-api-token'],
              username: action.username,
              password: action.password,
            })
          ),
          tap(action => {
            const { currentUser } = action;
            const { roles } = currentUser;
            const sortedRoles = roles.sort((role1, role2) => role1.localeCompare(role2));
            const joinedRoles = sortedRoles.join(':');

            switch (joinedRoles) {
              case 'default-user':
              case 'default-user:supervisor':
              case 'administrator:default-user:supervisor':
              case 'reviewer':
              case 'admin-user':
              case 'admin-user:super-user':
              case 'super-user':
                break;
              default:
                console.warn(`user's role or combination of roles is not supported: ${sortedRoles.join(', ')}`);
                this.snackbarService.showSnackbar(
                  'Your role or combination of roles is not supported. Please contact support to update your roles.',
                  'error',
                  7500
                );
            }
          }),
          tap(action => {
            this.localStorageService.setApiToken(action.apiToken);
            this.localStorageService.setUser(action.currentUser);
          }),
          catchError(error => this.serverErrorService.handleError(error))
        )
      )
    );
  });

  $signedIn = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.signedIn),
      switchMap(action =>
        this.versionService.getCurrentVersion().pipe(
          filter(response => {
            if (this.configService.getIsLocal()) {
              console.info('application is running in local mode. skipping version check');
              return true;
            }
            if (response.clientVersion.client_version !== this.configService.getVersion()) {
              this.localStorageService.clearApiToken();
              this.localStorageService.clearUser();
              this.sessionStorageService.setLoginCredentials({ username: action.username, password: action.password });
              console.info('client version has been updated');
              alert('A new version of the application is available. Click OK to refresh the page and update the application.');
              window.location.href = `${window.location.origin}${window.location.pathname}?refresh=${new Date().getTime()}`;
              return false;
            }
            return true;
          }),
          map(() => AuthActions.clientVersionValidated()),
          catchError(error => this.serverErrorService.handleError(error))
        )
      )
    );
  });

  signOut$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.signOut),
      switchMap(() => {
        resetAppState(this.store, false);
        return this.authService.signOut().pipe(
          tap(() => {
            this.localStorageService.clearApiToken();
            this.localStorageService.clearUser();
            this.localStorageService.clearServiceLocation();
          }),
          map(() => AuthActions.signedOut()),
          tap(() => this.router.navigate(['/auth/sign-in'])),
          tap(() => this.snackbarService.showSnackbar('You have successfully signed out.', 'info', 1000)),
          catchError(error => this.serverErrorService.handleError(error))
        );
      })
    );
  });

  private mapCurrentUser(currentUser: ApiCurrentUser): ClientCurrentUser {
    return {
      carrier: OssOptional.ofNullable(currentUser.carrier).orElseNull(),
      email: currentUser.email,
      first_name: currentUser.first,
      is_admin: OssOptional.ofNullable(currentUser.is_admin).orElse(false),
      is_carrier: OssOptional.ofNullable(currentUser.is_carrier).orElse(false),
      last_name: currentUser.last,
      rank: OssOptional.ofNullable(currentUser.rank).orElse(null),
      roles: currentUser.roles,
      service_location_ids: currentUser.serviceLocationIds,
      username: currentUser.username,
    };
  }

  constructor(
    private readonly actions$: Actions,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly snackbarService: SnackbarService,
    private readonly serverErrorService: ServerErrorService,
    private readonly localStorageService: LocalStorageService,
    private readonly store: Store<AppState>,
    private readonly configService: ConfigService,
    private readonly sessionStorageService: SessionStorageService,
    private readonly versionService: VersionService
  ) {}
}
